import { useCallback, useMemo } from 'react';

import type { VerifyCodeRes } from '@/api/gen/_model';
import { useVerifyCode } from '@/api/gen/phone-verification/phone-verification.gen';
import { isZepQuizApiError } from '@/shared/error';

import { VERIFICATION_ERROR_MESSAGES } from '../constants/errorMessages';

interface UseVerifySmsCodeOptions {
  onSuccess?: (response: VerifyCodeRes) => void;
  onError: (error: unknown) => void;
}

/**
 * SMS 인증번호 확인 훅
 * - 인증번호는 5분간 유효
 */
export const useVerifySmsCode = (options: UseVerifySmsCodeOptions) => {
  const mutation = useVerifyCode({
    mutation: {
      onSuccess: response => {
        if (response.data) {
          options?.onSuccess?.(response.data);
        }
      },
      onError: error => {
        options.onError(error);
      },
    },
  });

  const verifyCode = useCallback(
    (phoneNumber: string, code: string) => {
      mutation.mutate({
        data: { phoneNumber, code },
      });
    },
    [mutation],
  );

  const reset = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  const error = useMemo(() => {
    if (isZepQuizApiError(mutation.error)) {
      return VERIFICATION_ERROR_MESSAGES[mutation.error.status];
    }
    return null;
  }, [mutation.error]);

  return {
    verifyCode,
    reset,
    error,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
